import { useMemo } from 'react';
import { Banknote, CheckCircle2, FileText, User } from 'lucide-react';
import { MobileBottomSheet } from '@/components/ui/MobileBottomSheet';
import { MobileButton } from '@/components/ui/MobileButton';
import { cn } from '@/lib/utils';
import type { Party } from '@/app/types/party';
import type { Account } from '@/app/types/account';
import type { AllocationRow } from './OutstandingInvoicesList';

interface ConfirmPaymentSheetProps {
    open: boolean;
    onClose: () => void;
    onConfirm: () => void;
    isSubmitting: boolean;
    party: Party | null;
    account: Account | null;
    amount: number;
    allocations: AllocationRow[];
    referenceNumber: string;
    narration: string;
}

function formatRupees(n: number): string {
    return `₹${n.toLocaleString('en-IN', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
    })}`;
}

export function ConfirmPaymentSheet({
    open,
    onClose,
    onConfirm,
    isSubmitting,
    party,
    account,
    amount,
    allocations,
    referenceNumber,
    narration,
}: ConfirmPaymentSheetProps) {
    const selectedAllocations = useMemo(
        () =>
            allocations.filter(
                (a) => a.selected && Number(a.amount_to_allocate) > 0
            ),
        [allocations]
    );

    const totalAllocated = useMemo(
        () =>
            selectedAllocations.reduce(
                (sum, a) => sum + Number(a.amount_to_allocate || 0),
                0
            ),
        [selectedAllocations]
    );

    const unallocated = Math.max(amount - totalAllocated, 0);

    return (
        <MobileBottomSheet
            open={open}
            onClose={isSubmitting ? () => undefined : onClose}
            title="Review Payment"
            heightMode="auto"
            showCloseButton={!isSubmitting}
        >
            <div className="flex flex-col">
                {/* Amount hero */}
                <div className="px-5 pt-5 pb-4 text-center border-b border-border">
                    <p className="text-xs font-medium uppercase tracking-wider text-muted-foreground mb-1">
                        Total
                    </p>
                    <p className="text-4xl font-bold text-foreground">
                        {formatRupees(amount)}
                    </p>
                </div>

                {/* Party + Account summary */}
                <div className="px-4 py-3 space-y-2">
                    <SummaryRow
                        icon={<User className="h-4 w-4" />}
                        label="From"
                        value={party?.name ?? '—'}
                    />
                    <SummaryRow
                        icon={<Banknote className="h-4 w-4" />}
                        label="Into"
                        value={account?.name ?? '—'}
                    />
                    {referenceNumber && (
                        <SummaryRow
                            icon={<FileText className="h-4 w-4" />}
                            label="Reference"
                            value={referenceNumber}
                        />
                    )}
                </div>

                {/* Allocations */}
                <div className="px-4 pb-2">
                    <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-2">
                        Allocations
                    </p>
                    {selectedAllocations.length === 0 ? (
                        <div className="px-3 py-3 rounded-lg bg-card border border-border">
                            <p className="text-sm text-muted-foreground">
                                No invoice allocation — full amount will be credited to
                                the customer account.
                            </p>
                        </div>
                    ) : (
                        <div className="rounded-lg bg-card border border-border divide-y divide-border">
                            {selectedAllocations.map((a) => (
                                <div
                                    key={a.invoice_voucher_id}
                                    className="flex items-center justify-between gap-3 px-3 py-2.5"
                                >
                                    <div className="min-w-0">
                                        <p className="font-medium text-foreground truncate">
                                            {a.invoice_number}
                                        </p>
                                        <p className="text-xs text-muted-foreground">
                                            Due {formatRupees(a.amount_due)}
                                        </p>
                                    </div>
                                    <p className="font-semibold text-foreground whitespace-nowrap">
                                        {formatRupees(Number(a.amount_to_allocate || 0))}
                                    </p>
                                </div>
                            ))}
                            {unallocated > 0 && (
                                <div className="flex items-center justify-between gap-3 px-3 py-2.5 bg-muted/30">
                                    <p className="text-sm text-muted-foreground">
                                        Unallocated (credit to customer)
                                    </p>
                                    <p className="font-semibold text-green-500 whitespace-nowrap">
                                        {formatRupees(unallocated)}
                                    </p>
                                </div>
                            )}
                        </div>
                    )}
                </div>

                {/* Notes */}
                {narration && (
                    <div className="px-4 py-2">
                        <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-1">
                            Notes
                        </p>
                        <p className="text-sm text-foreground whitespace-pre-line">
                            {narration}
                        </p>
                    </div>
                )}

                {/* Actions */}
                <div
                    className={cn(
                        'flex items-center gap-3 px-4 py-4 border-t border-border bg-background'
                    )}
                >
                    <MobileButton
                        type="button"
                        variant="secondary"
                        size="lg"
                        disabled={isSubmitting}
                        onClick={onClose}
                        className="flex-1"
                    >
                        Cancel
                    </MobileButton>
                    <MobileButton
                        type="button"
                        size="lg"
                        isLoading={isSubmitting}
                        onClick={onConfirm}
                        icon={!isSubmitting ? <CheckCircle2 className="h-5 w-5" /> : undefined}
                        className="flex-1"
                    >
                        Confirm
                    </MobileButton>
                </div>
            </div>
        </MobileBottomSheet>
    );
}

interface SummaryRowProps {
    icon: React.ReactNode;
    label: string;
    value: string;
}

function SummaryRow({ icon, label, value }: SummaryRowProps) {
    return (
        <div className="flex items-center gap-3 p-3 bg-card rounded-lg border border-border">
            <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center text-primary flex-shrink-0">
                {icon}
            </div>
            <div className="min-w-0 flex-1">
                <p className="text-xs text-muted-foreground">{label}</p>
                <p className="font-medium text-foreground truncate">{value}</p>
            </div>
        </div>
    );
}
